/**
 * KAN-912: меню действий на пузыре нашего сообщения (пока один пункт
 * "Удалить"). Простой переключатель: кнопка открывает список role="menu",
 * закрывается по выбору пункта, клику вне меню и Escape. touch: кнопка
 * размером под палец (приложение /m).
 */
import { useEffect, useRef, useState } from 'react';
import { MoreVertical, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MessageActionsMenuProps {
  onDelete: () => void;
  touch?: boolean;
}

export function MessageActionsMenu({ onDelete, touch = false }: MessageActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0 self-start">
      <button
        type="button"
        aria-label="Действия с сообщением"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'inline-flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 active:bg-gray-200',
          touch ? 'h-11 w-11' : 'h-7 w-7'
        )}
      >
        <MoreVertical className={touch ? 'h-5 w-5' : 'h-4 w-4'} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-20 mt-1 min-w-[9rem] rounded-md border border-gray-200 bg-white py-1 shadow-md"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
            className={cn(
              'flex w-full items-center gap-2 px-3 text-left text-sm text-red-600 hover:bg-red-50 active:bg-red-100',
              touch ? 'min-h-[44px]' : 'py-1.5'
            )}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Удалить
          </button>
        </div>
      )}
    </div>
  );
}
